/**
 * Pure status helpers for the /sign/external/return landing (SIGNER-02).
 *
 * Kept out of `return.tsx` so the route file exports only its component
 * (react-refresh / fast-refresh hygiene) and so the mapping logic — including
 * the load-bearing HONESTY rule that the error/unknown view must NOT claim a
 * verified signature — is unit-testable without router context.
 */

export type ReturnStatus =
  | 'success'
  | 'declined'
  | 'cancelled'
  | 'timeout'
  | 'error'
  | 'unknown';

export interface ReturnView {
  eyebrow: string;
  eyebrowColor: string;
  title: string;
  body: string;
  role: 'status' | 'alert';
}

/** Collapse the provider's raw `status` query value into one of our states. */
export function normaliseStatus(raw: unknown): ReturnStatus {
  if (typeof raw !== 'string') return 'unknown';
  const v = raw.trim().toLowerCase();
  if (v === 'success' || v === 'signed' || v === 'completed' || v === 'complete') return 'success';
  if (v === 'declined' || v === 'decline') return 'declined';
  if (v === 'cancelled' || v === 'canceled' || v === 'cancel') return 'cancelled';
  if (v === 'timeout' || v === 'timedout' || v === 'expired') return 'timeout';
  if (v === 'error' || v === 'failed' || v === 'failure') return 'error';
  return 'unknown';
}

/** Map a status to the copy + ARIA role the landing renders. */
export function viewForStatus(status: ReturnStatus): ReturnView {
  switch (status) {
    case 'success':
      return {
        eyebrow: 'Signature recorded',
        eyebrowColor: '#2f5d44',
        title: 'Thank you. Your signature has been recorded.',
        body:
          'Your Advanced Electronic Signature has been submitted to the requesting firm. ' +
          'No further action is needed from you. You may now close this page.',
        role: 'status',
      };
    case 'declined':
      return {
        eyebrow: 'Signature declined',
        eyebrowColor: '#a05d5d',
        title: 'You declined to sign.',
        body:
          'Your decision to decline has been recorded and the requesting firm has been notified. ' +
          'You may now close this page.',
        role: 'status',
      };
    case 'cancelled':
      return {
        eyebrow: 'Signing cancelled',
        eyebrowColor: '#8a7f74',
        title: 'Signing was cancelled.',
        body:
          'You left the signature provider before completing your signature, so nothing has been ' +
          'signed. If this was a mistake, contact the firm that sent you the link to request a fresh one.',
        role: 'status',
      };
    case 'timeout':
      return {
        eyebrow: 'Session timed out',
        eyebrowColor: '#a05d5d',
        title: 'Your signing session timed out.',
        body:
          'For your security, signing sessions are time-limited and yours has now expired. ' +
          'Nothing has been signed. Please ask the firm that sent you the link to issue a fresh one.',
        role: 'alert',
      };
    case 'error':
    case 'unknown':
    default:
      return {
        eyebrow: 'Signing session ended',
        eyebrowColor: '#a05d5d',
        title: 'Your signing session has ended.',
        body:
          'We could not confirm the outcome of your signature from this page. If you completed ' +
          'signing at the provider, the requesting firm will have received it. If you are unsure, ' +
          'please contact the firm that sent you the link. Your data is safe.',
        role: 'alert',
      };
  }
}
